import React, { useCallback, useState, createRef, useEffect } from 'react'
import { ImageSourcePropType, Linking, useWindowDimensions } from 'react-native'
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import { NativeStackScreenProps } from '@react-navigation/native-stack'

import LoginActions from 'APP/Redux/LoginRedux'
import PatientActions from 'APP/Redux/PatientRedux'

import Config from 'APP/Config'

import { logger, trackDdEvent } from 'APP/Lib/Datadog'
import { REGISTRATION_ACTIONS } from 'APP/Lib/Datadog/constants'
import { getPrimaryLoginStrategy } from 'APP/Lib/ConfigHelpers'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import I18n, { isTranslationKey } from 'APP/I18n'
import usePrevious from 'APP/Hooks/usePrevious'
import useScreenView from 'APP/Hooks/useScreenView'

import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import Alert from 'APP/Converse/Alert'
import { CarouselDot, CarouselComponent, CarouselRef } from 'APP/Components/Carousel'
import DialogueBranding from 'APP/Components/DialogueBranding'
import { QRCodeAppModal } from 'APP/Components/QRCodeAppModal'

// Styling
import {
  StyledViewContainer,
  StyledViewHero,
  StyledViewCTAs,
  StyledViewImageContainer,
  StyledViewTextContainer,
  StyledViewCarousel,
  StyledViewDotsContainer,
  StyledImageHero,
  StyledViewButtonsContainer,
  StyledViewCTAsContainer,
  StyledViewQRContainer,
} from './style'
import { Images, Colors } from 'APP/Themes'
import Videos from '../../Themes/Videos'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import {
  getPendingManualConnection,
  setPendingManualConnection,
  clearPendingManualConnection,
} from 'APP/Lib/SeamlessDestination'
import { SlideVideo } from './SlideVideo/SlideVideo'
import { SlideVideoProps } from './SlideVideo/SlideVideo.types'

export const CTAS = {
  SIGNUP: 'SIGNUP',
  SIGNIN: 'SIGNIN',
  SSOSIGNIN: 'SSOSIGNIN',
}

const LOGIN_LONG_PRESS_DELAY_MS = 5000
const LOGIN_TIMEOUT = Config.LOGIN_TIMEOUT || 20000

export type SlideData = {
  isVideoSlide: boolean
  color: string
  image: ImageSourcePropType
  video: string
  title: string | undefined
  text: string | undefined
}

export type WelcomeScreenProps = NativeStackScreenProps<ReactNavigation.RootParamList, 'welcome'>

const WelcomeScreen = ({ route, navigation }: WelcomeScreenProps) => {
  const { openUniversalLogin } = route.params || {}
  const dispatch = useDispatch()
  const busy = useSelector((state) => state.login?.fetching || state.patient?.requestRunning)
  const loginConnection = useSelector((state) => state.login?.connection)
  const isLoggingIn = useSelector((state) => state.login?.isLoggingIn) // True during initial auth (username/password validation)
  const isOnboardingInProgress = useSelector((state) => state.login?.isOnboardingInProgress) // True during post-auth setup (profile, permissions, navigation)
  const isAuthInProgress = isLoggingIn || isOnboardingInProgress // Combined state for showing loading indicators

  const slideIndicies = Config.WELCOMESCREEN?.SLIDER_INDICES || [0, 1, 2, 3]

  const isSlideImageStretch =
    Config.WELCOMESCREEN && Config.WELCOMESCREEN.SLIDE_IMAGE_STRETCH && slideIndicies.length > 1

  const [actionType, setActionType] = useState<string | undefined>(undefined)
  const [configResolutionDone, setConfigResolutionDone] = useState(false)
  const [manualLoginConnection, setManualLoginConnection] = useState(() =>
    getPendingManualConnection()
  )

  const { isBigScreen, isSmallScreen } = useScreenView()

  const { height, width } = useWindowDimensions()
  const [shouldHideCarousel, setShouldHideCarousel] = useState(false)
  const heroWidth = isBigScreen ? width / 2 : width

  let heroHeight

  if (shouldHideCarousel) {
    heroHeight = 0
  } else if (isBigScreen) {
    heroHeight = height
  } else {
    heroHeight = height * (isSlideImageStretch ? 0.7 : 0.6)
  }

  const useSSOLogin = Config?.USE_DEFAULT_SSO_LOGIN === true

  const loginStrategy = getPrimaryLoginStrategy()
  const primaryStrategyConnection = loginStrategy?.connection
  const { enableSeamlessSsoDeeplink } = useSelector(selectFeatures)

  useEffect(() => {
    if (openUniversalLogin) {
      dispatch(LoginActions.loginRequest())
    }
  }, [])

  useEffect(() => {
    // Gated behind an LD flag so it can be disabled in prod without a redeploy.
    if (!enableSeamlessSsoDeeplink) return
    // Embedded (OneApp) authenticates through its host app, not this screen.
    if (isEmbeddedEnvironment() || openUniversalLogin) return

    // Branch deeplink set a connection (e.g. Sun Life) — auto-trigger a seamless login (no
    // click). On web this is a full-page redirect; on native the in-app browser opens and
    // returns tokens in place. With an active IdP session the member is signed in silently;
    // with no session the IdP shows its login form.
    // Only fires when the connection is the build's primary LOGIN_STRATEGIES entry (config
    // opt-in), so an action/login deeplink on other tenants keeps its original contract of
    // constraining the Login button rather than logging in unprompted.
    // The pendingManualConnection stash doubles as a one-shot marker: `connection` is
    // redux-persisted, so without the marker an abandoned attempt would re-trigger the login
    // on every subsequent mount and trap the member away from this screen.
    if (
      loginConnection &&
      loginConnection === primaryStrategyConnection &&
      !manualLoginConnection
    ) {
      setPendingManualConnection(loginConnection)
      setManualLoginConnection(loginConnection)
      dispatch(
        LoginActions.silentLoginRequestWithConnection(loginConnection, {
          triggerLogoutOnFailure: false,
        })
      )
    }
  }, [
    dispatch,
    openUniversalLogin,
    loginConnection,
    primaryStrategyConnection,
    manualLoginConnection,
    enableSeamlessSsoDeeplink,
  ])

  const prevBusy = usePrevious(busy)
  useEffect(() => {
    if (prevBusy && !busy) {
      setActionType(undefined)
    }
  }, [busy, prevBusy])

  useEffect(() => {
    // This timeout is necessary to resolve infinite loading
    // situations linked to SSO, refer to PR #5124 for more details
    let timeout: NodeJS.Timeout | undefined
    if (configResolutionDone || busy) {
      timeout = setTimeout(() => {
        setActionType(undefined)
        dispatch(LoginActions.loginRequestCancelled())
        dispatch(PatientActions.patientProfileFetchRequestCancelled())
      }, LOGIN_TIMEOUT)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [busy, configResolutionDone, dispatch])

  const slideData: SlideData[] = slideIndicies.map((indices, index) => {
    const isVideoSlide =
      !Array.isArray(indices) &&
      typeof indices === 'object' &&
      indices.type === Config.ASSET_TYPES?.video

    const colorName = `welcomeScreenBackgroundSlide${index}` as keyof typeof Colors
    const imageName = `welcomeSlide${index}` as keyof typeof Images
    const videoName = `welcomeSlide${index}` as keyof typeof Videos
    const titleKey = `WelcomeScreen.slide${index}Header`
    const textKey = `WelcomeScreen.slide${index}Text`
    return {
      isVideoSlide,
      color: Colors[colorName],
      image: Images[imageName],
      video: Videos[videoName],
      title: isTranslationKey(titleKey) ? I18n.t(titleKey) : undefined,
      text: isTranslationKey(textKey) ? I18n.t(textKey) : undefined,
    }
  })

  const carouselRef = createRef<CarouselRef | null>()

  const [slideIndex, setSlideIndex] = useState<number>(0)

  const [isQRCodeModalOpen, setQRCodeModalOpen] = useState<boolean>(false)

  const onDotPress = (index: number) => {
    if (index !== slideIndex) {
      carouselRef?.current?.scrollTo({ index })
      setSlideIndex(index)
    }
  }

  const renderSlideImage = (item: SlideData) => (
    <StyledViewImageContainer item={item}>
      {(!isWeb() || !isSmallScreen) && (
        <StyledImageHero
          source={item.image}
          resizeMode={isSlideImageStretch ? 'cover' : 'contain'}
        />
      )}
      <StyledViewTextContainer>
        <Typography variant="h2" color="text" align="center">
          {item.title}
        </Typography>
        <Typography color="text" align="center" variant="bodyNormal">
          {item.text}
        </Typography>
      </StyledViewTextContainer>
    </StyledViewImageContainer>
  )

  const renderSlideVideo = ({ color, video }: SlideVideoProps) => (
    <SlideVideo video={video} color={color} />
  )

  const renderHero = () => (
    <StyledViewCarousel slideIndex={slideIndex}>
      <CarouselComponent
        ref={carouselRef}
        width={heroWidth}
        height={heroHeight}
        data={slideData}
        onProgressChange={(_offsetProgress, absoluteProgress) => {
          if (
            carouselRef.current &&
            (absoluteProgress > 0.5 || carouselRef.current?.getCurrentIndex() === 0)
          ) {
            setSlideIndex(carouselRef.current.getCurrentIndex())
          }
        }}
        renderItem={({ item }) => {
          const { isVideoSlide } = item
          if (isWeb() && isSmallScreen && isVideoSlide) {
            setShouldHideCarousel(true)
            return null
          } else {
            setShouldHideCarousel(false)
          }
          return isVideoSlide ? renderSlideVideo(item) : renderSlideImage(item)
        }}
        enabled={slideData?.length > 1}
        loop={isWeb()}
        autoPlay={isWeb() && slideData?.length > 1}
      />
    </StyledViewCarousel>
  )

  const renderDots = () => (
    <StyledViewDotsContainer testID="DOTS_CONTAINER">
      {slideData.map((_, index) => {
        return (
          <CarouselDot
            index={index}
            key={index}
            onPress={onDotPress}
            selected={slideIndex === index}
          />
        )
      })}
    </StyledViewDotsContainer>
  )

  const withCTAPress = (ctaId: string, action: () => void) => async () => {
    if (actionType || busy) return

    setActionType(ctaId)
    setConfigResolutionDone(false)
    await action()
    setConfigResolutionDone(true)
  }

  const attemptSignUp = useCallback(async () => {
    // track the start of the registration journey
    trackDdEvent(REGISTRATION_ACTIONS.started)

    const customURL = Config?.CUSTOM_SIGNUP_URL?.[I18n.baseLocale]
    const canOpenURL = customURL ? await Linking.canOpenURL(customURL) : false
    if (!canOpenURL) {
      const connection = loginConnection || manualLoginConnection
      dispatch(
        connection
          ? LoginActions.signupRequestWithConnection(connection)
          : LoginActions.signupRequest()
      )

      return
    }

    if (isWeb()) {
      setConfigResolutionDone(true) // We should consider the action done on Web
      setActionType(undefined)
      window.location.href = customURL as string
    } else {
      try {
        await Linking.openURL(customURL as string)
      } catch {
        Alert.alert(
          I18n.t('CannotOpenCustomSignupUrl.title') ?? '',
          I18n.t('CannotOpenCustomSignupUrl.body') ?? '',
          [{ text: I18n.t('CannotOpenCustomSignupUrl.ok') ?? '' }]
        )
      }
    }
  }, [loginConnection, manualLoginConnection, dispatch])

  const attemptLogin = useCallback(() => {
    const connection = loginConnection || manualLoginConnection
    if (useSSOLogin && !connection) {
      if (loginStrategy?.connection) {
        dispatch(
          LoginActions.loginRequestWithConnection(loginStrategy.connection, {
            triggerLogoutOnFailure: false,
          })
        )
        return
      }
      logger.error('LOGIN_STRATEGIES missing primary entry')
      setActionType(undefined)
      Alert.alert(
        I18n.t('WelcomeScreen.errorTitle') ?? '',
        I18n.t('WelcomeScreen.errorSubtitle') ?? '',
        [{ text: I18n.t('WelcomeScreen.errorOk') ?? '' }]
      )
      return
    }
    if (manualLoginConnection) {
      clearPendingManualConnection()
    }
    dispatch(
      connection ? LoginActions.loginRequestWithConnection(connection) : LoginActions.loginRequest()
    )
  }, [loginConnection, manualLoginConnection, dispatch, useSSOLogin, loginStrategy])

  const renderCTAs = () => {
    const showActivityIndicator = configResolutionDone || busy
    // Show loading when user clicks login OR when auth is processing in background (but not when clicking other buttons)
    const showLoginActivityIndicator =
      (showActivityIndicator && actionType === CTAS.SIGNIN) || (isAuthInProgress && !actionType)

    const showSignupActivityIndicator = showActivityIndicator && actionType === CTAS.SIGNUP
    const showTexts = isBigScreen || (!isBigScreen && slideData.length === 1)

    return (
      <StyledViewCTAsContainer>
        <StyledViewTextContainer>
          {showTexts && (
            <>
              <Typography color="text" variant="h3" align="center">
                {I18n.t('WelcomeScreen.title')}
              </Typography>
              <Typography color="text" variant="bodyNormal" align="center">
                {I18n.t('WelcomeScreen.subtitle')}
              </Typography>
            </>
          )}
        </StyledViewTextContainer>
        <StyledViewButtonsContainer>
          <Button
            variant="primary"
            title={I18n.t('WelcomeScreen.signUp') ?? ''}
            widthVariant="full"
            onPress={withCTAPress(CTAS.SIGNUP, attemptSignUp)}
            isLoading={showSignupActivityIndicator}
            analyticsName="WelcomeScreen.signUp"
            testID="SIGNUP_BTN"
          />
          <Button
            variant="secondary"
            title={I18n.t('WelcomeScreen.logIn') ?? ''}
            widthVariant="full"
            onPress={withCTAPress(CTAS.SIGNIN, attemptLogin)}
            isLoading={showLoginActivityIndicator}
            analyticsName="WelcomeScreen.logIn"
            onLongPress={() => {
              // Only show the tenant switcher if there are other tenants to switch to
              if (Config.HOST_OVERRIDES && Object.keys(Config.HOST_OVERRIDES).length > 0) {
                // @ts-expect-error - TODO: Screen name not in RootParamList type custom.d.ts
                navigation.navigate('tenantSwitcherScreen')
              }
            }}
            delayLongPress={LOGIN_LONG_PRESS_DELAY_MS}
            testID="LOGIN_BTN"
          />
        </StyledViewButtonsContainer>
        <DialogueBranding />
      </StyledViewCTAsContainer>
    )
  }

  return (
    <StyledViewContainer slideIndex={slideIndex}>
      <StyledViewHero>
        {renderHero()}
        {slideData?.length > 1 && renderDots()}
      </StyledViewHero>
      <StyledViewCTAs hasDialogueBranding={Config.DIALOGUE_BRANDING_ENABLED}>
        {renderCTAs()}
        {isWeb() && (
          <StyledViewQRContainer testID="QR_CODE_CONTAINER">
            <Button
              testID="QR_CODE_BTN"
              variant="tertiary"
              title={I18n.t('WelcomeScreen.downloadApp') ?? ''}
              widthVariant={isBigScreen ? 'full' : 'fixed'}
              analyticsName="WelcomeScreen.downloadApp"
              icon="qr-code"
              iconPosition="right"
              iconVariant="materialIcons"
              onPress={() => setQRCodeModalOpen(true)}
            />
          </StyledViewQRContainer>
        )}
      </StyledViewCTAs>
      <QRCodeAppModal isVisible={isQRCodeModalOpen} onClose={() => setQRCodeModalOpen(false)} />
    </StyledViewContainer>
  )
}

export default WelcomeScreen
